import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { formatLocalIso } from '../../utils/date.utils';

export interface AvailabilityRuleDto {
  availabilityRuleId?: string;
  merchantStaffId?: string;
  weekDay: number;
  startTime: string;
  endTime: string;
  /**
   * @deprecated El step real se calcula ahora en el backend desde el Concept
   * (Duration + BufferBefore + BufferAfter). Campo mantenido como opcional
   * solo por compatibilidad con respuestas legacy.
   */
  slotStepMinutes?: number;
  effectiveFrom?: string;
  effectiveTo?: string;
}

export interface AvailabilityRulesBulkDto {
  weekDays: number[];
  startTime: string;
  endTime: string;
  /** @deprecated Ver AvailabilityRuleDto.slotStepMinutes. */
  slotStepMinutes?: number;
  effectiveFrom?: string;
  effectiveTo?: string;
}

export interface AvailabilityExceptionDto {
  availabilityExceptionId?: string;
  merchantStaffId?: string;
  exceptionDate: string;
  isClosed: boolean;
  startTime?: string;
  endTime?: string;
}

export interface TimeOffDto {
  timeOffId?: string;
  merchantStaffId?: string;
  startAt: string;
  endAt: string;
  reason?: string;
}

export interface BookingBlockDto {
  bookingBlockId?: string;
  merchantStaffId?: string;
  startAt: string;
  endAt: string;
  reason?: string;
  isActive: boolean;
}

export interface StaffAvailabilityContextDto {
  merchantStaffId: string;
  employeeExternalId: string;
  displayName: string;
  email: string;
  availabilityRules: AvailabilityRuleDto[];
  availabilityExceptions: AvailabilityExceptionDto[];
  timeOffs: TimeOffDto[];
  bookingBlocks: BookingBlockDto[];
}

export interface SingleResponse<T> {
  isSuccess: boolean;
  value?: T;
  error?: {
    statusCode: number;
    messages: string[];
  };
}

export interface PaginableResponse<T> {
  isSuccess: boolean;
  values?: T[];
  total?: number;
  error?: {
    statusCode: number;
    messages: string[];
  };
}

@Injectable({
  providedIn: 'root'
})
export class LocationAvailabilityService {
  private apiUrl = `${environment.apiUrl}/api/v2/booking/availability`;

  constructor(private http: HttpClient) {}

  getStaffAvailabilityContext(
    locationId: string,
    employeeId: string
  ): Observable<SingleResponse<StaffAvailabilityContextDto>> {
    return this.http.get<SingleResponse<StaffAvailabilityContextDto>>(
      `${this.apiUrl}/locations/${locationId}/employees/${employeeId}/context`
    );
  }

  getAvailabilityRules(
    locationId: string,
    staffId: string
  ): Observable<PaginableResponse<AvailabilityRuleDto>> {
    return this.http.get<PaginableResponse<AvailabilityRuleDto>>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/rules`
    );
  }

  createAvailabilityRule(
    locationId: string,
    staffId: string,
    rule: AvailabilityRuleDto
  ): Observable<SingleResponse<string>> {
    return this.http.post<SingleResponse<string>>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/rules`,
      rule
    );
  }

  createAvailabilityRulesBulk(
    locationId: string,
    staffId: string,
    bulk: AvailabilityRulesBulkDto
  ): Observable<SingleResponse<string[]>> {
    return this.http.post<SingleResponse<string[]>>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/rules/bulk`,
      bulk
    );
  }

  updateAvailabilityRule(
    locationId: string,
    staffId: string,
    ruleId: string,
    rule: AvailabilityRuleDto
  ): Observable<SingleResponse<AvailabilityRuleDto>> {
    return this.http.put<SingleResponse<AvailabilityRuleDto>>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/rules/${ruleId}`,
      rule
    );
  }

  deleteAvailabilityRule(
    locationId: string,
    staffId: string,
    ruleId: string
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/rules/${ruleId}`
    );
  }

  getAvailabilityExceptions(
    locationId: string,
    staffId: string,
    from?: Date,
    to?: Date
  ): Observable<PaginableResponse<AvailabilityExceptionDto>> {
    let params = new HttpParams();
    if (from) {
      params = params.set('from', formatLocalIso(from));
    }
    if (to) {
      params = params.set('to', formatLocalIso(to));
    }

    return this.http.get<PaginableResponse<AvailabilityExceptionDto>>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/exceptions`,
      { params }
    );
  }

  createAvailabilityException(
    locationId: string,
    staffId: string,
    exception: AvailabilityExceptionDto
  ): Observable<SingleResponse<string>> {
    return this.http.post<SingleResponse<string>>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/exceptions`,
      exception
    );
  }

  updateAvailabilityException(
    locationId: string,
    staffId: string,
    exceptionId: string,
    exception: AvailabilityExceptionDto
  ): Observable<SingleResponse<AvailabilityExceptionDto>> {
    return this.http.put<SingleResponse<AvailabilityExceptionDto>>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/exceptions/${exceptionId}`,
      exception
    );
  }

  deleteAvailabilityException(
    locationId: string,
    staffId: string,
    exceptionId: string
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/exceptions/${exceptionId}`
    );
  }

  getTimeOffs(
    locationId: string,
    staffId: string,
    from?: Date,
    to?: Date
  ): Observable<PaginableResponse<TimeOffDto>> {
    let params = new HttpParams();
    if (from) {
      params = params.set('from', formatLocalIso(from));
    }
    if (to) {
      params = params.set('to', formatLocalIso(to));
    }

    return this.http.get<PaginableResponse<TimeOffDto>>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/timeoffs`,
      { params }
    );
  }

  createTimeOff(
    locationId: string,
    staffId: string,
    timeOff: TimeOffDto
  ): Observable<SingleResponse<string>> {
    return this.http.post<SingleResponse<string>>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/timeoffs`,
      timeOff
    );
  }

  updateTimeOff(
    locationId: string,
    staffId: string,
    timeOffId: string,
    timeOff: TimeOffDto
  ): Observable<SingleResponse<TimeOffDto>> {
    return this.http.put<SingleResponse<TimeOffDto>>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/timeoffs/${timeOffId}`,
      timeOff
    );
  }

  deleteTimeOff(
    locationId: string,
    staffId: string,
    timeOffId: string
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/timeoffs/${timeOffId}`
    );
  }

  getBookingBlocks(
    locationId: string,
    staffId: string,
    from?: Date,
    to?: Date
  ): Observable<PaginableResponse<BookingBlockDto>> {
    let params = new HttpParams();
    if (from) {
      params = params.set('from', formatLocalIso(from));
    }
    if (to) {
      params = params.set('to', formatLocalIso(to));
    }

    return this.http.get<PaginableResponse<BookingBlockDto>>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/blocks`,
      { params }
    );
  }

  createBookingBlock(
    locationId: string,
    staffId: string,
    block: BookingBlockDto
  ): Observable<SingleResponse<string>> {
    return this.http.post<SingleResponse<string>>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/blocks`,
      block
    );
  }

  updateBookingBlock(
    locationId: string,
    staffId: string,
    blockId: string,
    block: BookingBlockDto
  ): Observable<SingleResponse<BookingBlockDto>> {
    return this.http.put<SingleResponse<BookingBlockDto>>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/blocks/${blockId}`,
      block
    );
  }

  deleteBookingBlock(
    locationId: string,
    staffId: string,
    blockId: string
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/locations/${locationId}/staff/${staffId}/blocks/${blockId}`
    );
  }
}
